/**
 * The connected apps the `@` menu offers beside files and policies.
 *
 * Two sources, one list: the person's own plugin connections (Gmail, Drive,
 * Slack — `accountScope: personal`) and the organisation's integrations
 * (HubSpot, SharePoint — `accountScope: workspace`). Only what is actually
 * connected and switched on is offered: tagging an app the agent cannot reach
 * would only produce a "please connect it" reply.
 *
 * @module components/chat/composerConnectionItems
 */
import type { ComposerConnectionScope } from "@amoreg/shared/composerTrigger";

import type { ConnectionView } from "../../state/connections";
import type { Integration } from "../../state/governance";

export interface ComposerConnectionItem {
  readonly id: string;
  readonly type: "connection";
  readonly connectorId: string;
  readonly scope: ComposerConnectionScope;
  readonly label: string;
  readonly description: string;
  readonly iconUrl: string | null;
  readonly toolkitSlug: string | null;
}

const CONNECTION_ITEM_LIMIT = 12;

function matchesQuery(
  query: string,
  ...candidates: ReadonlyArray<string | null | undefined>
): boolean {
  if (query.length === 0) return true;
  return candidates.some((candidate) => candidate?.toLowerCase().includes(query) ?? false);
}

export function buildComposerConnectionItems(input: {
  readonly connections: ReadonlyArray<
    Pick<
      ConnectionView,
      "id" | "name" | "connectionStatus" | "userEnabled" | "iconUrl" | "toolkitSlug"
    >
  >;
  readonly integrations: ReadonlyArray<
    Pick<Integration, "id" | "name" | "connected" | "enabled" | "iconUrl" | "toolkitSlug">
  >;
  readonly query: string;
}): ComposerConnectionItem[] {
  const query = input.query.trim().toLowerCase();
  const personal = input.connections
    .filter((connection) => connection.connectionStatus === "connected" && connection.userEnabled)
    .filter((connection) =>
      matchesQuery(query, connection.name, connection.id, connection.toolkitSlug),
    )
    .map((connection): ComposerConnectionItem => ({
      id: `connection:personal:${connection.id}`,
      type: "connection",
      connectorId: connection.id,
      scope: "personal",
      label: connection.name,
      description: "Connected app",
      iconUrl: connection.iconUrl,
      toolkitSlug: connection.toolkitSlug ?? null,
    }));
  const workspace = input.integrations
    .filter((integration) => integration.connected && integration.enabled)
    .filter((integration) =>
      matchesQuery(query, integration.name, integration.id, integration.toolkitSlug),
    )
    .map((integration): ComposerConnectionItem => ({
      id: `connection:workspace:${integration.id}`,
      type: "connection",
      connectorId: integration.id,
      scope: "workspace",
      label: integration.name,
      description: "Workspace integration",
      iconUrl: integration.iconUrl,
      toolkitSlug: integration.toolkitSlug,
    }));
  return [...personal, ...workspace].slice(0, CONNECTION_ITEM_LIMIT);
}
